import { EPS, clamp } from './numbers.js';
import { cross, distance, dot, lerp, sub, type Vec2 } from './vec.js';

export interface Segment {
  readonly a: Vec2;
  readonly b: Vec2;
}

export const segmentLength = (s: Segment): number => distance(s.a, s.b);

/** Parameter t ∈ [0,1] of the closest point on the segment. Degenerate segments return 0. */
export function closestParamOnSegment(p: Vec2, s: Segment): number {
  const ab = sub(s.b, s.a);
  const lenSq = dot(ab, ab);
  if (lenSq < EPS * EPS) return 0;
  return clamp(dot(sub(p, s.a), ab) / lenSq, 0, 1);
}

export function closestPointOnSegment(p: Vec2, s: Segment): Vec2 {
  return lerp(s.a, s.b, closestParamOnSegment(p, s));
}

export function distancePointToSegment(p: Vec2, s: Segment): number {
  return distance(p, closestPointOnSegment(p, s));
}

function orientation(a: Vec2, b: Vec2, c: Vec2): number {
  const v = cross(sub(b, a), sub(c, a));
  if (Math.abs(v) < EPS) return 0;
  return v > 0 ? 1 : -1;
}

function onSegment(p: Vec2, s: Segment): boolean {
  return (
    p.x <= Math.max(s.a.x, s.b.x) + EPS &&
    p.x >= Math.min(s.a.x, s.b.x) - EPS &&
    p.y <= Math.max(s.a.y, s.b.y) + EPS &&
    p.y >= Math.min(s.a.y, s.b.y) - EPS
  );
}

/** True if the segments touch or cross (including collinear overlap). */
export function segmentsIntersect(s1: Segment, s2: Segment): boolean {
  const o1 = orientation(s1.a, s1.b, s2.a);
  const o2 = orientation(s1.a, s1.b, s2.b);
  const o3 = orientation(s2.a, s2.b, s1.a);
  const o4 = orientation(s2.a, s2.b, s1.b);

  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(s2.a, s1)) return true;
  if (o2 === 0 && onSegment(s2.b, s1)) return true;
  if (o3 === 0 && onSegment(s1.a, s2)) return true;
  if (o4 === 0 && onSegment(s1.b, s2)) return true;
  return false;
}

/**
 * True only if the segments cross at a single interior point of both. Touching endpoints
 * and collinear overlap do not count, so shapes placed flush against each other pass.
 */
export function segmentsProperlyIntersect(s1: Segment, s2: Segment): boolean {
  const o1 = orientation(s1.a, s1.b, s2.a);
  const o2 = orientation(s1.a, s1.b, s2.b);
  const o3 = orientation(s2.a, s2.b, s1.a);
  const o4 = orientation(s2.a, s2.b, s1.b);
  return o1 * o2 < 0 && o3 * o4 < 0;
}

export function distanceSegmentToSegment(s1: Segment, s2: Segment): number {
  if (segmentsIntersect(s1, s2)) return 0;
  return Math.min(
    distancePointToSegment(s1.a, s2),
    distancePointToSegment(s1.b, s2),
    distancePointToSegment(s2.a, s1),
    distancePointToSegment(s2.b, s1),
  );
}
